import { createSelector } from "@reduxjs/toolkit";
import { SCORING_TYPE_KEYS } from "constants/scoring";
import { format } from "date-fns";
import { isEmpty, isNil, omit, pickBy } from "lodash";

import { selectIssueIdsFilter } from "../edit/selectors";
import { selectScoringState } from "../selectors";

export const selectScoringAnalysisState = createSelector(
  [selectScoringState],
  (scoring) => scoring?.analysis || {}
);

export const selectScoringContent = createSelector(
  [selectScoringAnalysisState],
  (analysis) => analysis.content
);

export const selectScoringUnavailable = createSelector(
  [selectScoringAnalysisState],
  (analysis) => Boolean(analysis.scoringUnavailable)
);

export const selectIsRescanning = createSelector(
  [selectScoringAnalysisState],
  (analysis) => Boolean(analysis.isRescanning)
);

export const selectFilteredScoringContent = createSelector(
  [selectScoringContent, selectIssueIdsFilter],
  (content, issueIdsFilter) => {
    if (isNil(issueIdsFilter) || isEmpty(issueIdsFilter)) return content;

    return content.reduce((acc, paragraph) => {
      const { paragraphStats } = paragraph;
      const restOfStats = omit(paragraphStats, SCORING_TYPE_KEYS);

      const filteredIssues = pickBy(paragraphStats, (_, key) => {
        return issueIdsFilter.includes(key) && paragraphStats[key]?.length > 0;
      });

      return isEmpty(filteredIssues)
        ? acc
        : acc.concat({
            ...paragraph,
            paragraphStats: {
              ...restOfStats,
              ...filteredIssues,
            },
          });
    }, []);
  }
);

export const selectScoringTree = createSelector(
  [selectScoringAnalysisState],
  (analysis) => analysis.tree || []
);

export const selectAnalysisMetadata = createSelector(
  [selectScoringAnalysisState],
  (analysis) => analysis.metadata || {}
);

export const selectScanDate = createSelector(
  [selectAnalysisMetadata],
  (metadata) => {
    const { scanDate } = metadata || {};
    if (isNil(scanDate) || isEmpty(scanDate)) return null;

    const { dayOfMonth, hour, minute, monthValue, year } = scanDate;
    const d = new Date();
    d.setFullYear(year);
    d.setMonth(monthValue - 1);
    d.setDate(dayOfMonth);
    d.setHours(hour);
    d.setMinutes(minute);

    return d;
  }
);

export const selectFormattedScanDate = createSelector(
  [selectScanDate],
  (scanDate) => {
    if (isNil(scanDate)) return null;

    return format(scanDate, "M.d.yyyy 'at' H:mm");
  }
);

export const selectScanStats = createSelector(
  [selectAnalysisMetadata],
  (metadata) => metadata?.scanStats
);

export const selectScanStatsDerived = createSelector(
  [selectAnalysisMetadata],
  (metadata) => metadata?.scanStatsDerived
);

export const selectScoreThresholds = createSelector(
  [selectAnalysisMetadata],
  (metadata) => metadata?.thresholds
);

export const selectIsScoringRevision = createSelector(
  [selectAnalysisMetadata],
  (metadata) => Boolean(metadata?.isRevision)
);

export const selectMetadataLanguage = createSelector(
  [selectAnalysisMetadata],
  (metadata) => metadata?.language
);

export const selectIsLanguageEnglish = createSelector(
  [selectMetadataLanguage],
  (language) => language?.toLowerCase() === "en"
);

export const selectTotalWatchwordsHits = createSelector(
  [selectAnalysisMetadata],
  (metadata) => metadata?.watchWordResults?.totalOccurrences ?? 0
);

export const selectTotalIgnoredTerms = createSelector(
  [selectAnalysisMetadata],
  (metadata) => metadata?.whiteListResults?.totalOccurrences ?? 0
);

export const selectCurrentSelectedTermHash = createSelector(
  [selectScoringAnalysisState],
  (analysis) => {
    return analysis.selectedTermHash || null;
  }
);
